import type {
  ImagePrompt,
  MultiFrameTransition,
  RecaptionPrompt,
  ReferencePrompt,
  TaskInputPrompts,
} from '@/app/types';
import { hasUploadedImagePrompt } from './temp-image-uri';
import { stringifyTextPromptWithReference } from './text-prompt-with-reference';

type AudioPromptReferenceWithVoiceId = NonNullable<TaskInputPrompts['reference']> & {
  voice_id?: string;
};

/**
 * 构建单个图片的 TaskInputPrompts
 *
 * @param imagePrompt 图片 prompt
 * @param taskType 任务类型
 * @param options 可选配置
 * @param options.multiFrameIndex 多帧转场场景下的图片索引，如果提供则添加 multi_frame 字段
 * @returns TaskInputPrompts 或 undefined
 */
const buildImagePrompt = (
  imagePrompt: ImagePrompt,
  options?: { multiFrameIndex?: number },
): TaskInputPrompts | undefined => {
  if (!hasUploadedImagePrompt(imagePrompt)) {
    return undefined;
  }

  const prompt: TaskInputPrompts = {
    type: 'image',
    content: imagePrompt.selectedImage,
    src_imgs: [imagePrompt.sourceImage],
  };

  // 多帧转场场景，添加 multi_frame 字段
  if (options?.multiFrameIndex !== undefined) {
    prompt.multi_frame = {
      single: {
        index: options.multiFrameIndex,
      },
    };
  }

  // 有选择区域时，需要传裁剪的范围
  if (imagePrompt.selectedImageRange) {
    prompt.selected_region = {
      top_left: {
        x: imagePrompt.selectedImageRange[0],
        y: imagePrompt.selectedImageRange[1],
      },
      bottom_right: {
        x: imagePrompt.selectedImageRange[2],
        y: imagePrompt.selectedImageRange[3],
      },
    };
  }

  return prompt;
};

/**
 * 将表单输入的图片 prompts 转换为提交任务接口所需格式
 *
 * @param imagePrompts 图片 prompts
 * @param taskType 任务类型
 * @returns 提交任务接口所需格式
 */
export const convertImagePromptsFormToAPI = (imagePrompts: (ImagePrompt | null)[]): TaskInputPrompts[] => {
  if (!imagePrompts) return [];

  return imagePrompts
    .map((p) => {
      if (!p) return undefined;

      return buildImagePrompt(p);
    })
    .filter(Boolean) as TaskInputPrompts[];
};

/**
 * 将多帧转场表单数据转换为提交任务接口所需格式
 *
 * @param imagePrompts 图片 prompts 数组（FormImagePrompt 格式）
 * @param multiFrameTransitions 多帧转场配置
 * @param taskType 任务类型
 * @returns 提交任务接口所需格式
 */
export const convertMultiFrameToAPI = (
  imagePrompts: Array<{ prompt?: ImagePrompt | null } | null>,
  multiFrameTransitions: MultiFrameTransition[] | undefined,
): TaskInputPrompts[] => {
  const imageInputWithMultiFrame = imagePrompts
    .map((p, index) => {
      if (!p?.prompt) return undefined;

      const prompt = buildImagePrompt(p.prompt, {
        multiFrameIndex: index,
      });

      return prompt;
    })
    .filter(Boolean) as TaskInputPrompts[];

  // 如果没有转场或转场为空，直接返回图片
  if (!multiFrameTransitions || multiFrameTransitions.length === 0) {
    return imageInputWithMultiFrame;
  }

  // 有转场的情况，添加转场 prompt（如果有写 prompt）
  const transitionPrompts: TaskInputPrompts[] = [];
  multiFrameTransitions.forEach((transition) => {
    if (transition.prompt?.trim()) {
      transitionPrompts.push({
        type: 'text',
        content: transition.prompt,
        multi_frame: {
          range: {
            from: transition.from,
            to: transition.to,
          },
        },
      });
    }
  });

  return [...imageInputWithMultiFrame, ...transitionPrompts];
};

/**
 * 将表单输入的 referencePrompts 转换为提交任务接口所需格式
 *
 * @param referencePrompts 包含主体/图片的 referencePrompts
 * @returns 提交任务接口所需格式
 */
export const convertReferencePromptsFormToAPI = (referencePrompts: ReferencePrompt[]): TaskInputPrompts[] => {
  const prompts: TaskInputPrompts[] = [];

  // 处理引用的图片或素材
  referencePrompts.forEach((ref) => {
    if (ref.status !== 'done' || !ref.prompt) return;

    if (ref.type === 'image') {
      // 对于图片类型，使用 buildImagePrompt 处理
      const convertedImage = buildImagePrompt(ref.prompt);
      if (convertedImage) {
        prompts.push({
          ...convertedImage,
          type: 'image',
          name: ref.name, // 添加 name 字段，与文本中的 [@name] 对应
          reference: ref.reference,
        });
      }
    } else if (ref.type === 'video') {
      // 对于视频类型，使用 prompt.video 作为 content
      prompts.push({
        type: 'video',
        content: ref.prompt.video,
        name: ref.name,
        cover_uri: ref.prompt.coverImage,
      });
    } else if (ref.type === 'audio') {
      if (ref.prompt.isElementVoiceReference && ref.prompt.originElementVoiceIsMy && !ref.prompt.originElementVoiceId)
        return;

      if (ref.prompt.isElementVoiceReference && ref.prompt.originElementVoiceIsMy) {
        const reference: AudioPromptReferenceWithVoiceId = {
          voice_id: ref.prompt.originElementVoiceId,
        };
        prompts.push({
          type: 'audio',
          content: ref.prompt.src,
          name: ref.name,
          reference,
        } as TaskInputPrompts);
      } else {
        prompts.push({
          type: 'audio',
          content: ref.prompt.src,
          name: ref.name,
        });
      }
    } else if (ref.type === 'element') {
      const element = ref.prompt;
      const material: NonNullable<TaskInputPrompts['material']> = {
        id: element.id || '',
        version: element.version || '',
        share_id: element.share_meta?.status === 'approved' ? element.share_meta?.id : undefined,
      };

      prompts.push({
        type: 'material',
        content: element.components?.[0]?.content || '',
        name: ref.name,
        material,
      });
    }
  });

  return prompts;
};

/**
 * 将专业模式表单数据转换为提交任务接口所需格式
 *
 * @param recaption 专业模式表单数据
 * @returns 提交任务接口所需格式
 */
export const convertProModeFormToAPI = (recaption: RecaptionPrompt): TaskInputPrompts => {
  const {
    subject,
    environment,
    style,
    motionLevel,
    shotSize,
    focalLength,
    composition,
    cameraAngle,
    specialFilmingTechnique,
    cameraMovement,
  } = recaption;
  return {
    type: 'text',
    content: JSON.stringify({
      Subject: stringifyTextPromptWithReference(subject),
      Environment: stringifyTextPromptWithReference(environment),
      Style: style.join(', '),
      'Motion Level': motionLevel?.join(', '),
      'Shot Size': shotSize?.join(', '),
      'Focal Length': focalLength?.join(', '),
      Composition: composition?.join(', '),
      'Camera Angle': cameraAngle?.join(', '),
      'Special Filming Technique': specialFilmingTechnique?.join(', '),
      'Camera Movement': cameraMovement?.join(', '),
    }),
  };
};
